import { useEffect, useState } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Edit,
  Play,
  Trash2,
  Clock,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Circle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { SOPEditorDialog } from "@/components/sop/SOPEditorDialog";
import { StartRunDialog } from "@/components/run/StartRunDialog";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface SOPStep {
  id: string;
  title: string;
  instruction_rich_text: string | null;
  step_type: string;
  required_proof_type: string;
  sla_minutes: number | null;
  is_critical: boolean;
  order_index: number;
}

interface SOP {
  id: string;
  title: string;
  description: string | null;
  status: string;
  tags: string[];
  estimated_duration_minutes: number | null;
  version: number;
  created_at: string;
  last_published_at: string | null;
}

const SOPDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user, organizationId } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const [sop, setSop] = useState<SOP | null>(null);
  const [steps, setSteps] = useState<SOPStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (id) {
      fetchSOPDetails();
    }
  }, [id]);

  const fetchSOPDetails = async () => {
    setLoading(true);
    try {
      const { data: sopData, error: sopError } = await supabase
        .from("sop_templates")
        .select("*")
        .eq("id", id)
        .single();

      if (sopError) throw sopError;
      setSop(sopData);

      const { data: stepsData, error: stepsError } = await supabase
        .from("sop_steps")
        .select("*")
        .eq("sop_template_id", id)
        .order("order_index", { ascending: true });

      if (stepsError) throw stepsError;
      setSteps(stepsData || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("sop_templates").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "SOP Deleted",
        description: "The SOP has been deleted successfully.",
      });

      navigate("/app/sops");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = async () => {
    if (!id) return;
    try {
      const { error } = await supabase
        .from("sop_templates")
        .update({
          status: "ACTIVE",
          last_published_at: new Date().toISOString(),
          version: (sop?.version || 0) + 1,
        })
        .eq("id", id);

      if (error) throw error;

      setSop((prev) =>
        prev
          ? {
              ...prev,
              status: "ACTIVE",
              last_published_at: new Date().toISOString(),
              version: prev.version + 1,
            }
          : null
      );

      toast({
        title: "SOP Published",
        description: "The SOP is now active and can be used in runs.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-500/10 text-green-500";
      case "DRAFT":
        return "bg-amber-500/10 text-amber-500";
      case "ARCHIVED":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="container py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">SOP not found</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/app/sops")}>
              Back to SOPs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-6 md:py-8 space-y-4 sm:space-y-6 px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/sops")} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1 sm:mb-2">
              <h1 className="text-xl sm:text-2xl font-bold truncate">{sop.title}</h1>
              <Badge className={getStatusColor(sop.status)}>{sop.status}</Badge>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground line-clamp-2">{sop.description || "No description"}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0 ml-11 sm:ml-0">
          {sop.status === "DRAFT" && (
            <Button onClick={handlePublish} size="sm" className="flex-1 sm:flex-initial">
              <CheckCircle className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Publish</span>
            </Button>
          )}
          <Button variant="outline" onClick={() => setRunDialogOpen(true)} size="sm" className="flex-1 sm:flex-initial">
            <Play className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Start Run</span>
          </Button>
          <Button variant="outline" onClick={() => setEditorOpen(true)} size="sm">
            <Edit className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Edit</span>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete SOP</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this SOP? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
            <div className="text-xs sm:text-sm text-muted-foreground">Steps</div>
            <div className="text-xl sm:text-2xl font-bold">{steps.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
            <div className="text-xs sm:text-sm text-muted-foreground">Est. Duration</div>
            <div className="text-xl sm:text-2xl font-bold">
              {sop.estimated_duration_minutes ? `${sop.estimated_duration_minutes}m` : "--"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
            <div className="text-xs sm:text-sm text-muted-foreground">Version</div>
            <div className="text-xl sm:text-2xl font-bold">v{sop.version}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
            <div className="text-xs sm:text-sm text-muted-foreground">Published</div>
            <div className="text-xl sm:text-2xl font-bold">
              {sop.last_published_at
                ? format(new Date(sop.last_published_at), "MMM d")
                : "--"}
            </div>
          </CardContent>
        </Card>
      </div>

      {sop.tags && sop.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sop.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Steps ({steps.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {steps.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No steps defined. Click Edit to add steps.
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={step.id}>
                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{step.title}</span>
                        {step.is_critical && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Critical
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {step.step_type}
                        </Badge>
                        {step.required_proof_type !== "NONE" && (
                          <Badge variant="outline" className="text-xs">
                            Proof: {step.required_proof_type}
                          </Badge>
                        )}
                      </div>
                      {step.instruction_rich_text && (
                        <p className="text-sm text-muted-foreground">
                          {step.instruction_rich_text}
                        </p>
                      )}
                      {step.sla_minutes && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          SLA: {step.sla_minutes} minutes
                        </p>
                      )}
                    </div>
                  </div>
                  {index < steps.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SOPEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        organizationId={organizationId}
        sopId={id}
        onSuccess={fetchSOPDetails}
      />

      <StartRunDialog
        open={runDialogOpen}
        onOpenChange={setRunDialogOpen}
        organizationId={organizationId}
        userId={user.id}
        onSuccess={() => navigate("/app/runs")}
      />
    </div>
  );
};

export default SOPDetail;
